import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { buildDataUrl } from '../../../helper/api';

import { isInViewport } from '../../../helper';

export const FloorPlanContext = React.createContext();

const initialState = {
  activeMarker: {
    id: null,
    headline: null,
    plan: null
  },
  hoveredPlan: '',
  expanded: false,
  expandedPlan: '',
  waitingLayer: false,
  waitingLayerHeadline: null,
  data: null,
  loading: true,
  error: false
};

function reducer(state, action) {
  switch (action.type) {
    case 'hoverPlan':
      return { ...state, hoveredPlan: action.payload };
    case 'expandPlan':
      return { ...state, expanded: true, expandedPlan: action.payload };
    case 'collapsePlan':
      return { ...state, expanded: false, expandedPlan: '', hoveredPlan: '' };
    case 'showWaitingLayer':
      return { ...state, waitingLayer: action.payload };
    case 'setWaitingLayerHeadline':
      return { ...state, waitingLayerHeadline: action.payload };
    case 'setActiveMarker':
      return { ...state, activeMarker: action.payload };
    case 'setData':
      return { ...state, data: action.payload };
    case 'setLoading':
      return { ...state, loading: action.payload };
    case 'setError':
      return { ...state, error: action.payload };
    default:
      throw new Error(`${action.type} is undefined.`);
  }
}

const FloorPlanProvider = ({ children, dataUrl }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const hoverPlan = plan => {
    dispatch({ type: 'hoverPlan', payload: plan });
  };

  const expandPlan = plan => {
    dispatch({ type: 'expandPlan', payload: plan });
  };

  const collapsePlan = () => {
    dispatch({ type: 'collapsePlan' });
  };

  const showWaitingLayer = type => {
    dispatch({ type: 'showWaitingLayer', payload: type });
  };

  const fetchData = async () => {
    try {
      dispatch({ type: 'setLoading', payload: true });
      console.log('debug before fetch', dataUrl);
      const floorplanUrl = buildDataUrl(dataUrl);
      const floorPlanResponse = await fetch(floorplanUrl);
      const floorPlanData = await floorPlanResponse.json();
      dispatch({ type: 'setData', payload: floorPlanData });
    } catch (e) {
      dispatch({ type: 'setError', payload: true });
      if (e) {
        throw new Error('Something went wrong!' + dataUrl + ' --- ' + buildDataUrl(dataUrl));
      }
    } finally {
      dispatch({ type: 'setLoading', payload: false });
    }
  };

  const setWaitingLayerHeadline = headline => {
    dispatch({ type: 'setWaitingLayerHeadline', payload: headline });
  };

  const updateActiveMarker = () => {
    const exhibitionTeasers = document.querySelectorAll('.exhibitionContainer__teaser');
    exhibitionTeasers.forEach(element => {
      const elementHeadline = element.querySelector('h2');
      if (isInViewport(elementHeadline)) {
        dispatch({
          type: 'setActiveMarker',
          payload: {
            id: element.id,
            plan: element.getAttribute('data-category'),
            headline: element.querySelector('a').title
          }
        });
      }
    });
  };

  const throttledUpdateActiveMarker = throttle(updateActiveMarker, 200);

  useEffect(() => {
    window.addEventListener('scroll', throttledUpdateActiveMarker);

    return () => {
      window.removeEventListener('scroll', throttledUpdateActiveMarker);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [dataUrl]);

  return (
    <FloorPlanContext.Provider
      value={{
        ...state,
        hoverPlan,
        expandPlan,
        collapsePlan,
        showWaitingLayer,
        setWaitingLayerHeadline
      }}
    >
      {children}
    </FloorPlanContext.Provider>
  );
};

FloorPlanProvider.propTypes = {
  children: PropTypes.node.isRequired,
  dataUrl: PropTypes.string.isRequired
};

export const floorPlanConsumer = FloorPlanContext.Consumer;

export default FloorPlanProvider;
